// components/PaperPlaneCanvas/PaperPlaneCanvas.jsx
import React, { useEffect, useRef, useState } from 'react';
import './PaperPlaneCanvas.scss';

const PaperPlaneCanvas = () => {
  const canvasRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const gameStateRef = useRef({
    plane: null,
    bullets: [],
    rocks: [],
    score: 0,
    mouseX: 0,
    mouseY: 0,
    lastRockSpawn: 0,
    isSpacePressed: false,
    animationId: null
  });

  const [score, setScore] = useState(0);
  const [isGameActive, setIsGameActive] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Don't run the game on mobile devices
    if (isMobile) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const gameState = gameStateRef.current;

    // Initialize canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // Initialize plane
    const createPlane = () => {
      gameState.plane = {
        x: canvas.width / 2,
        y: canvas.height / 2,
        targetX: canvas.width / 2,
        targetY: canvas.height / 2,
        rotation: 0,
        size: 25,
        trail: []
      };
    };

    // Event handlers
    const handleMouseMove = (e) => {
      gameState.mouseX = e.clientX;
      gameState.mouseY = e.clientY;
      if (gameState.plane) {
        gameState.plane.targetX = gameState.mouseX;
        gameState.plane.targetY = gameState.mouseY;
      }
    };

    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!gameState.isSpacePressed) {
          gameState.isSpacePressed = true;
          setIsGameActive(true);
        } else {
          // If game is already active, shoot bullet
          shoot();
        }
      } else if (e.code === 'Escape') {
        e.preventDefault();
        // Deactivate game mode
        gameState.isSpacePressed = false;
        setIsGameActive(false);
        // Clear bullets and rocks
        gameState.bullets = [];
        gameState.rocks = [];
        // Reset score
        gameState.score = 0;
        setScore(0);
      }
    };

    const handleKeyUp = (e) => {
      // Don't turn off space mode - keep it activated once pressed
    };

    const handleClick = (e) => {
      if (gameState.isSpacePressed) {
        shoot();
      }
    };

    // Game functions
    const updatePlane = () => {
      if (!gameState.plane) return;

      const dx = gameState.plane.targetX - gameState.plane.x;
      const dy = gameState.plane.targetY - gameState.plane.y;
      
      // Smoother, more direct movement without wobbling
      gameState.plane.x += dx * 0.08;
      gameState.plane.y += dy * 0.08;
      gameState.plane.rotation = Math.atan2(dy, dx);

      // Always update trail (irrespective of game activation)
      gameState.plane.trail.push({ x: gameState.plane.x, y: gameState.plane.y });
      if (gameState.plane.trail.length > 25) {
        gameState.plane.trail.shift();
      }
    };

    const drawPlane = () => {
      if (!gameState.plane) return;

      // Always draw dotted trail (irrespective of game activation)
      if (gameState.plane.trail.length > 2) {
        ctx.save();
        ctx.setLineDash([5, 10]);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        
        ctx.beginPath();
        ctx.moveTo(gameState.plane.trail[0].x, gameState.plane.trail[0].y);
        
        for (let i = 1; i < gameState.plane.trail.length; i++) {
          const alpha = (i / gameState.plane.trail.length) * 0.5;
          ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.lineTo(gameState.plane.trail[i].x, gameState.plane.trail[i].y);
          ctx.stroke();
          
          if (i < gameState.plane.trail.length - 1) {
            ctx.beginPath();
            ctx.moveTo(gameState.plane.trail[i].x, gameState.plane.trail[i].y);
          }
        }
        ctx.restore();
      }

      // Only draw the actual plane when game is active
      if (gameState.isSpacePressed) {
        ctx.save();
        ctx.translate(gameState.plane.x, gameState.plane.y);
        ctx.rotate(gameState.plane.rotation);
        
        // Spaceship mode - blue glow (always since only visible when active)
        ctx.fillStyle = 'rgba(74, 158, 255, 0.8)';
        ctx.shadowColor = '#4a9eff';
        ctx.shadowBlur = 15;

        ctx.beginPath();
        ctx.moveTo(gameState.plane.size * 0.8, 0);
        ctx.lineTo(-gameState.plane.size * 0.6, -gameState.plane.size * 0.4);
        ctx.lineTo(-gameState.plane.size * 0.2, -gameState.plane.size * 0.1);
        ctx.lineTo(-gameState.plane.size * 0.8, 0);
        ctx.lineTo(-gameState.plane.size * 0.2, gameState.plane.size * 0.1);
        ctx.lineTo(-gameState.plane.size * 0.6, gameState.plane.size * 0.4);
        ctx.closePath();
        
        ctx.fill();
        ctx.restore();
      }
    };

    const shoot = () => {
      if (!gameState.plane) return;
      
      gameState.bullets.push({
        x: gameState.plane.x,
        y: gameState.plane.y,
        vx: Math.cos(gameState.plane.rotation) * 8,
        vy: Math.sin(gameState.plane.rotation) * 8,
        life: 100
      });
    };

    const updateBullets = () => {
      for (let i = gameState.bullets.length - 1; i >= 0; i--) {
        const bullet = gameState.bullets[i];
        bullet.x += bullet.vx;
        bullet.y += bullet.vy;
        bullet.life--;

        if (bullet.life <= 0 || bullet.x < 0 || bullet.x > canvas.width || 
            bullet.y < 0 || bullet.y > canvas.height) {
          gameState.bullets.splice(i, 1);
        }
      }
    };

    const drawBullets = () => {
      ctx.save();
      ctx.fillStyle = '#4a9eff';
      ctx.shadowColor = '#4a9eff';
      ctx.shadowBlur = 5;
      
      for (const bullet of gameState.bullets) {
        ctx.beginPath();
        ctx.arc(bullet.x, bullet.y, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const spawnRock = () => {
      if (Date.now() - gameState.lastRockSpawn > 2000 && gameState.isSpacePressed) {
        const rock = {
          x: Math.random() * canvas.width,
          y: -50,
          vx: (Math.random() - 0.5) * 2,
          vy: Math.random() * 2 + 1,
          size: Math.random() * 30 + 20,
          rotation: 0,
          rotationSpeed: (Math.random() - 0.5) * 0.1,
          vertices: []
        };

        // Generate random rock shape
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const radius = rock.size * (0.7 + Math.random() * 0.3);
          rock.vertices.push({
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius
          });
        }

        gameState.rocks.push(rock);
        gameState.lastRockSpawn = Date.now();
      }
    };

    const updateRocks = () => {
      for (let i = gameState.rocks.length - 1; i >= 0; i--) {
        const rock = gameState.rocks[i];
        rock.x += rock.vx;
        rock.y += rock.vy;
        rock.rotation += rock.rotationSpeed;

        if (rock.y > canvas.height + 100) {
          gameState.rocks.splice(i, 1);
        }
      }
    };

    const drawRocks = () => {
      // Draw rocks normally (no globalAlpha to hide them)
      ctx.save();
      ctx.globalAlpha = 0.1; 
      for (const rock of gameState.rocks) {
        ctx.save();
        ctx.translate(rock.x, rock.y);
        ctx.rotate(rock.rotation);
        ctx.fillStyle = 'rgba(150, 150, 150, 0.8)'; // More visible
        ctx.strokeStyle = 'rgba(200, 200, 200, 0.6)'; // Visible outline
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(rock.vertices[0].x, rock.vertices[0].y);
        for (let i = 1; i < rock.vertices.length; i++) {
          ctx.lineTo(rock.vertices[i].x, rock.vertices[i].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
    };

    const checkCollisions = () => {
      for (let i = gameState.bullets.length - 1; i >= 0; i--) {
        for (let j = gameState.rocks.length - 1; j >= 0; j--) {
          const bullet = gameState.bullets[i];
          const rock = gameState.rocks[j];
          const dx = rock.x - bullet.x;
          const dy = rock.y - bullet.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < rock.size) {
            gameState.bullets.splice(i, 1);
            gameState.rocks.splice(j, 1);
            gameState.score += 10;
            setScore(gameState.score);
            break;
          }
        }
      }
    };

    const gameLoop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw rocks first (in background with very low opacity)
      updateRocks();
      drawRocks();
      
      // Then draw plane and bullets (in foreground)
      updatePlane();
      drawPlane();
      
      updateBullets();
      drawBullets();
      
      spawnRock();
      checkCollisions();
      
      gameState.animationId = requestAnimationFrame(gameLoop);
    };

    // Initialize everything
    resizeCanvas();
    createPlane();

    // Add event listeners
    window.addEventListener('resize', resizeCanvas);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('click', handleClick);

    // Start game loop
    gameLoop();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('click', handleClick);
      
      if (gameState.animationId) {
        cancelAnimationFrame(gameState.animationId);
      }
    };
  }, [isMobile]);

  // Don't render anything on mobile
  if (isMobile) {
    return null;
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className="paper-plane-canvas"
      />
      {isGameActive && (
        <div className="game-score">
          Score: {score}
        </div>
      )}
      {/* Always show instructions on desktop */}
      <div className="game-instructions">
        Press SPACE to activate/shoot • Click to shoot • ESC to deactivate
      </div>
    </>
  );
};

export default PaperPlaneCanvas;